import moment from 'moment';
import { TipoComissao, CHEQUE, CARTAO, DINHEIRO, DEPOSITO, USUARIO_AUTO, DIAS_TOLERANCIA_VENCIMENTO_CHEQUE_DEPOSITO, CondicoesEspeciaisStatus } from '~/constant';

// Pagamentos no simulador agora usam shape venda real direto (via
// PagamentosSinalGravarVenda + ModalFormaPgto). Sem necessidade de transformação.
// BOLETO/CARTAO com qtd > 1 já vêm expandidos em N items pelo handleSave.

// Helpers internos --------------------------------------------------

function formatarData(d) {
  if (!d) return null;
  if (typeof d === 'string') return d;
  return moment(d).format('YYYY-MM-DD');
}

// Arredonda valor monetário pra 2 casas decimais (backend Setpar geralmente
// rejeita valores com mais de 2 casas).
function r2(v) {
  return Math.round((v || 0) * 100) / 100;
}

function obterUltimoVencimentoParcelas(listas) {
  return listas
    .reduce(function(acc, lista) { return acc.concat(lista || []); }, [])
    .map(function(p) { return formatarData(p && p.vencimento); })
    .filter(Boolean)
    .sort()
    .pop() || null;
}

function obterMesesCarenciaCondicaoEspecial(condicoesAplicadas) {
  return (
    condicoesAplicadas &&
    condicoesAplicadas.financiamento &&
    condicoesAplicadas.financiamento.isAtivo
  ) ? (parseInt(condicoesAplicadas.financiamento.meses, 10) || 0) : 0;
}

function formatarDataBR(data) {
  return moment(data).format('DD/MM/YYYY');
}

function formatarDataMMAAAA(s) {
  if (!s) return null;
  // entrada esperada MM/AAAA -> retorna YYYY-MM-01
  var m = String(s).match(/^(\d{2})\/(\d{4})$/);
  if (!m) return null;
  return m[2] + '-' + m[1] + '-01';
}

// Espelha a transformação de Store/Vendas/actions.js:296-447 (gravarVenda).
// Para CHEQUE: { vencimento, tipo_parcela, valor, cheque: <item inteiro> }.
// Para CARTAO: { vencimento, tipo_parcela, valor, cartao: { codigo, numero_documento } }.
// Para outros: { vencimento, tipo_parcela, valor }.
function toFormaPagamentoPayload(p) {
  var venc = p.vencimento;
  // Garante string ISO se for moment/Date
  if (venc && typeof venc !== 'string') venc = formatarData(venc);
  var base = {
    vencimento: venc,
    tipo_parcela: p.tipo_parcela,
    valor: p.valor,
  };
  if (p.tipo_parcela === CHEQUE) {
    // gravarVenda real faz `cheque: sinal` — espalha o item inteiro como
    // sub-objeto. Replicamos: o item flat já contém banco/agencia/etc.
    base.cheque = Object.assign({}, p, { vencimento: venc });
  } else if (p.tipo_parcela === CARTAO) {
    var cartaoObj = p.cartao && typeof p.cartao === 'object' ? p.cartao : null;
    base.cartao = {
      codigo: cartaoObj ? cartaoObj.codigo : (p.cartao || p.codigo),
      numero_documento: cartaoObj ? cartaoObj.numero_documento : p.numero_documento,
    };
  }
  return base;
}

function toParcelaIntermediariaPayload(p, indice, taxa) {
  // Espelha shape do reducer real (Store/Vendas/Financiamento/reducer.js:50-58):
  // { valor_presente, valor, vencimento, parcela, tipo_parcela, tipo_intermediaria, tipo_parcela_financiamento }
  return {
    valor_presente: p.valorPresente || 0,
    valor: p.valorFuturo || p.valor || 0,
    vencimento: formatarData(p.vencimento),
    parcela: indice + 1,
    tipo_parcela: p.tipo || 'Intermediária',
    tipo_intermediaria: true,
    tipo_parcela_financiamento: 'Intermediária',
    taxa: taxa || 0,
  };
}

function toParcelaCondicionantePayload(c, indice, taxa) {
  // Backend CondicoesVenda.PeriodicidadeBalao é int? — 1 = Construção, 2 = Adimplência.
  // String 'CONSTRUCAO'/'ADIMPLENCIA' causava erro de deserialização no backend.
  var isConstrucao = c.tipo && String(c.tipo).toLowerCase().indexOf('constru') >= 0;
  return {
    valor_presente: c.valorPresente || 0,
    valor: c.valorFuturo || 0,
    vencimento: formatarData(c.vencimento),
    parcela: 1,
    periodicidade_balao: isConstrucao ? 1 : 2,
    codigo_intermediaria: c.codigoIntermediaria || null,
    taxa: taxa || 0,
    tipo_parcela_financiamento: 'Condicionante',
  };
}

function toCondicoesEspeciaisPayload(c, extras) {
  if (!c || !c.descricao) return {};
  var ex = extras || {};
  var payload = {
    financiamento: {
      avaliacao_bancaria: ex.avaliacaoBancaria ? 1 : 0,
      prazo_financiamento_meses: (c.financiamento && c.financiamento.meses) || 0,
      data_prazo_financiamento: (c.financiamento && c.financiamento.dataPrazo)
        ? formatarData(c.financiamento.dataPrazo)
        : null,
      data_termino_construcao: formatarDataMMAAAA(ex.dataTerminoConstrucao),
    },
    saldo_correcao: {
      data_saldo: c.saldoCorrecao ? formatarDataMMAAAA(c.saldoCorrecao.data) : null,
      indice_correcao: ex.indiceCorrecao || null,
      valor_saldo_correcao: (c.saldoCorrecao && c.saldoCorrecao.valor) || 0,
    },
    valor_lote: {
      porcentagem_desconto: (c.valorLote && c.valorLote.porcentagem) || 0,
      valor_sugerido: (c.valorLote && c.valorLote.valorSugerido) || 0,
    },
    is_isencao: !!c.isIsencao,
    descricao: c.descricao || '',
  };
  if (c.status) payload.status = c.status;
  return payload;
}

// API pública -------------------------------------------------------

// Monta o payload exato esperado pelo POST
//   /venda/empreendimento/{codigoFamilia}/prospecto/{codigoProspecto}
// a partir do estado local do simulador em modo "Gerar Venda".
//
// Espelha gravarVenda action de Store/Vendas/actions.js:296-486.
// Mono-lote (ativos: [1]).
export function simulatorStateToVendaPayload(state) {
  var contextoVenda = state.contextoVenda || {};
  var empreendimento = contextoVenda.empreendimento || {};
  var dados = state.dados || {};
  var valorVendaEditado = state.valorVendaEditado || 0;
  var dataBase = state.dataBase;
  var financiamentoPlano = state.financiamentoPlano || { qtd: 0 };
  var saldoFinanciarLiquido = state.saldoFinanciarLiquido || 0;
  // Pagamentos: duas listas separadas igual à venda real.
  // O usuário já preencheu cada uma no componente PagamentosSinalSplit antes de abrir o modal.
  // Não há splitting aqui — as listas chegam prontas.
  var pagamentosSinalIncorp = state.pagamentosIncorp || [];
  var pagamentosSinalMesa = state.pagamentosMesa || [];
  var parcelasIntermediarias = state.parcelasIntermediariasManuais || [];
  var condicionantes = state.condicionantesAplicadas || [];
  var condicoesAplicadas = state.condicoesAplicadas || null;
  var condicoesExtras = state.condicoesExtras || {};
  var codigoConstrutor = state.codigoConstrutor || null;
  var codigoPontoVenda = state.codigoPontoVenda || null;
  var vendaInterna = !!state.vendaInterna;
  var contratoDigital = !!state.contratoDigital;
  var opcaoRecebimentoBoletos = state.opcaoRecebimentoBoletos || 1;
  var isParcelasNaoConcomitantes = !!state.isParcelasNaoConcomitantes;
  var autorizacao = state.autorizacao || { justificativa: '', codigo_usuario: '', senha: '' };
  var codigoProposta = state.codigoProposta || null;
  var isContraproposta = !!state.contraproposta;

  // codigoAtivo é a fonte da verdade (vem do SelecaoAtivo via state).
  // dados (resposta de buscarSimulacaoFinanciamento) NÃO inclui o codigo —
  // tem mapa1/mapa2/valor_a_vista/etc. mas o identificador do lote precisa
  // vir do contexto da seleção.
  var codigoAtivo = state.codigoAtivo || dados.codigo || null;

  // Tenta achar o ativo no empreendimento carregado pra ter campos completos
  // (gravarVenda espalha ...ativo no payload).
  var ativoEmpreendimento = (empreendimento.ativos || []).find(function(a) {
    return String(a.codigo) === String(codigoAtivo);
  }) || null;

  var ativoBase = ativoEmpreendimento || {};

  // Cálculo do sinal — duas fontes possíveis:
  //   1. ativoEmpreendimento (vem de buscarFamilia) → cálculo via helpers
  //   2. dados (vem de buscarSimulacaoFinanciamento) → campos diretos
  // Quando o usuário muda o VV, ajusta proporcional ao VV original.
  var ratio = (dados.valor_a_vista > 0)
    ? (valorVendaEditado / dados.valor_a_vista)
    : 1;

  // valor_sinal_*: refletem o que foi PAGO (soma dos pagamentos alocados),
  // não o esperado. Backend valida cobertura mínima depois.
  var totalPagamentosIncorp = pagamentosSinalIncorp.reduce(function(s, p) {
    return s + (p.valor || 0);
  }, 0);
  var totalPagamentosMesa = pagamentosSinalMesa.reduce(function(s, p) {
    return s + (p.valor || 0);
  }, 0);
  var valorSinalEsperado = totalPagamentosIncorp + totalPagamentosMesa;
  var valorSinalIncorpEsperado = totalPagamentosIncorp;
  var valorSinalImobEsperado = totalPagamentosMesa;

  var taxa = dados.taxa_parcela_intermediaria || 0;

  // Vencimento da 1ª parcela do financiamento: prioridade
  //   1) state.vencimentoFinanciamento (manual, se o corretor preencheu)
  //   2) último sinal + carência CE + 1 mês, ou última programada + 1 mês
  //   3) dataBase + carência CE + 1 mês (fallback sem parcelas anteriores)
  var dataPrimeiraParcela;
  if (state.vencimentoFinanciamento) {
    dataPrimeiraParcela = moment(state.vencimentoFinanciamento);
  } else {
    dataPrimeiraParcela = moment(calcularVencimentoFinanciamentoDefault(
      dataBase,
      pagamentosSinalIncorp,
      pagamentosSinalMesa,
      condicoesAplicadas,
      state.parcelasEmSeries
    ));
  }

  // Ativo: espalha campos do ativoEmpreendimento (como ...ativo da venda real)
  // e sobrescreve com o que o simulador calculou.
  var ativoPayload = Object.assign({}, ativoBase, {
    codigo: codigoAtivo,
    quadra: dados.mapa1,
    lote: dados.mapa2,
    valor_sinal: r2(valorSinalEsperado),
    valor_venda: r2(valorVendaEditado),
    // *_original = valor ESPERADO pela tabela (antes de qualquer pagamento).
    // Igual à venda real: salvarSinal envia original separado do pago.
    valor_sinal_original: r2((dados.valor_entrada || 0) * ratio),
    valor_sinal_incorporadora_original: r2((dados.entrada_incorporadora || 0) * ratio),
    valor_sinal_imobiliaria_original: r2((dados.entrada_imobiliaria || 0) * ratio),
    // *sem _original = o que foi efetivamente pago (soma das formas de pagamento).
    valor_sinal_incorporadora: r2(valorSinalIncorpEsperado),
    valor_sinal_imobiliaria: r2(valorSinalImobEsperado),
    desconto: {
      valor_sinal: r2(Math.max(0, (dados.valor_entrada || 0) * ratio - (totalPagamentosIncorp + totalPagamentosMesa))),
      valor_venda: r2(Math.max(0, (dados.valor_a_vista || 0) - valorVendaEditado)),
      aplicou_cheque_bonus_sinal: !!state.aplicouChequeBonusSinal,
      aplicou_cheque_bonus_valor_venda: false,
      codigo_solicitacao_desconto: null,
    },
    // shortForm-like: { quantidade_parcelas, valor_presente, valor, vencimento }
    financiamento: {
      quantidade_parcelas: financiamentoPlano.qtd || 0,
      valor_presente: r2(saldoFinanciarLiquido),
      valor: r2(financiamentoPlano.valorParcela || 0),
      vencimento: dataPrimeiraParcela.format('YYYY-MM-DD'),
    },
    parcelas_intermediarias: parcelasIntermediarias.map(function(p, i) {
      return toParcelaIntermediariaPayload(p, i, taxa);
    }),
    parcelas_em_series: (state.parcelasEmSeries || []),
    parcelas_condicionantes: condicionantes.map(function(c, i) {
      return toParcelaCondicionantePayload(c, i, taxa);
    }),
    divisao_comissao: state.divisaoComissao || null,
  });

  var sinalLista = pagamentosSinalIncorp;
  var sinalMesaLista = pagamentosSinalMesa;

  return {
    codigo_familia: empreendimento.codigo,
    // Corretor responsável pela venda (W-0075): corretor logado (auto) ou
    // selecionado pelo ADM+673. Backend valida alçada/GE/comissão usando este user.
    codigo_usuario: state.codigoCorretorAtivo || 0,
    tabela_selecionada: dados.codigo_tabela || null,
    autorizacao: {
      justificativa: autorizacao.justificativa || '',
      // Se user não preencher autorizador manualmente, manda USUARIO_AUTO=1
      // pra que o backend faça as validações com esse user de sistema
      // (quem tem o user logado sem GE cadastrado fica sem ter como autorizar).
      codigo_usuario: autorizacao.codigo_usuario || USUARIO_AUTO,
      senha: autorizacao.senha || '',
    },
    ativos: [ativoPayload],
    sinal: sinalLista.map(toFormaPagamentoPayload),
    sinal_mesa: sinalMesaLista.map(toFormaPagamentoPayload),
    data: formatarData(dataBase),
    codigo_ponto_venda: codigoPontoVenda,
    codigo_proposta: codigoProposta,
    codigo_construtor: codigoConstrutor,
    venda_interna: vendaInterna,
    contrato_digital: contratoDigital,
    condicoes_especiais: toCondicoesEspeciaisPayload(condicoesAplicadas, condicoesExtras),
    contraproposta: isContraproposta,
    is_parcelas_nao_concomitantes: isParcelasNaoConcomitantes,
    opcao_recebimento_boletos: opcaoRecebimentoBoletos,
  };
}

// Validações pré-submit. Retorna array de erros (vazio = ok).
export function validarPayloadAntesSubmit(payload, state) {
  var erros = [];
  var contextoVenda = (state && state.contextoVenda) || {};
  var prospecto = contextoVenda.prospecto || {};
  var empreendimento = contextoVenda.empreendimento || {};
  var requerSplit = empreendimento.tipo_recebimento_comissao === TipoComissao.RetidaCorretor;
  var dados = (state && state.dados) || {};

  if (!payload.codigo_familia) erros.push('Empreendimento não carregado');
  if (!payload.codigo_usuario) erros.push('Corretor não informado');
  if (!payload.tabela_selecionada) erros.push('Tabela de preço não selecionada');
  if (!payload.ativos[0].codigo) erros.push('Lote não selecionado');
  if (!payload.ativos[0].valor_venda || payload.ativos[0].valor_venda <= 0) {
    erros.push('Valor de venda inválido');
  }
  if (state.financiamentoPlano == null) {
    erros.push('Plano de financiamento não selecionado');
  }
  if (!payload.data) erros.push('Data da venda obrigatória');
  // Não validamos:
  // - codigo_conta do prospecto (consultarProspectos não retorna esse campo;
  //   na venda real tem porque vem de outro endpoint. Backend valida server-side)
  // - codigo_construtor / codigo_ponto_venda (não pedidos no simulador — defaults)

  var totalSinal = (payload.sinal || []).reduce(function(s, p) { return s + (p.valor || 0); }, 0);
  var totalMesa = (payload.sinal_mesa || []).reduce(function(s, p) { return s + (p.valor || 0); }, 0);
  if (totalSinal + totalMesa <= 0) {
    erros.push('Adicione ao menos uma forma de pagamento ao sinal');
  }
  if (requerSplit) {
    // Recalcula requerido (mesma lógica do index.js que decide se cada card aparece).
    // Se o card daquele lado nem foi exibido (requerido=0), não exige preenchimento.
    var ratioVV = (dados.valor_a_vista > 0) ? (state.valorVendaEditado || 0) / dados.valor_a_vista : 1;
    var reqMesa = Math.round((dados.entrada_imobiliaria || 0) * ratioVV * 100) / 100;
    var reqIncorp = Math.round((dados.entrada_incorporadora || 0) * ratioVV * 100) / 100;
    if (reqMesa > 0 && totalMesa <= 0) erros.push('Sinal Mesa (Imobiliária) não preenchido');
    if (reqIncorp > 0 && totalSinal <= 0) erros.push('Sinal Incorporadora não preenchido');
  }

  // Sinal parcial (abaixo do esperado pela tabela) é permitido: a gravação segue para
  // o fluxo de autorização (usuário + senha) e, sem credenciais, vai para aprovação da
  // diretoria. A entrada mínima (sinal_minimo) abaixo continua sendo exigida.

  const sinalMinimo = state && state.dados && state.dados.sinal_minimo ? state.dados.sinal_minimo : null;
  if (sinalMinimo && sinalMinimo > 0) {
    const dataVenda = payload.data ? new Date(payload.data) : new Date();
    const limiteTolerancia = new Date(dataVenda);
    limiteTolerancia.setDate(limiteTolerancia.getDate() + DIAS_TOLERANCIA_VENCIMENTO_CHEQUE_DEPOSITO);
    const todasParcelas = (payload.sinal || []).concat(payload.sinal_mesa || []);
    const totalEntradaObrigatoria = todasParcelas.reduce((acc, p) => {
      const tipo = p.tipo_parcela;
      if (tipo === DINHEIRO || tipo === CARTAO) return acc + (p.valor || 0);
      if (tipo === CHEQUE || tipo === DEPOSITO) {
        const venc = p.vencimento ? new Date(p.vencimento) : null;
        if (venc && venc <= limiteTolerancia) return acc + (p.valor || 0);
      }
      return acc;
    }, 0);
    if (totalEntradaObrigatoria < sinalMinimo - 0.01) {
      erros.push(
        `A entrada informada à vista (dinheiro e cartão, ou depósito e cheque em até ${DIAS_TOLERANCIA_VENCIMENTO_CHEQUE_DEPOSITO} dias), ` +
        `de R$ ${totalEntradaObrigatoria.toFixed(2)}, é menor do que a requerida no empreendimento, de R$ ${sinalMinimo.toFixed(2)}`
      );
    }
  }

  // Condições especiais — se aplicadas, exige descrição
  var ce = payload.condicoes_especiais;
  var temCE = ce && ce.descricao;
  if (payload.contraproposta && !temCE) {
    erros.push('Contraproposta exige uma condição especial carregada');
  }
  if (ce && Object.keys(ce).length > 0 && !ce.descricao) {
    erros.push('Descrição de condição especial obrigatória');
  }
  if (temCE && ce.descricao.replace(/\s/g, '').length < 20) {
    erros.push('Descrição de condição especial: mín. 20 caracteres não-espaço');
  }
  // Condição especial no simulador segue o fluxo comum da venda: sem credenciais,
  // grava aguardando aprovação conforme alçada; com credenciais, autoriza na hora.
  var exigeAutorizacaoCondicoes = temCE && ce.status !== CondicoesEspeciaisStatus.SolicitacaoAprovada;
  if (exigeAutorizacaoCondicoes) {
    if (!payload.autorizacao.justificativa || payload.autorizacao.justificativa.length < 5) {
      erros.push('Condições especiais aplicadas — preencha justificativa');
    }
  }

  var ativoPrincipal = payload.ativos && payload.ativos.length > 0 ? payload.ativos[0] : null;
  var financiamento = ativoPrincipal && ativoPrincipal.financiamento;
  var temProgramadas = !!(state && state.parcelasEmSeries && state.parcelasEmSeries.length > 0);
  var mesesCarenciaCE = obterMesesCarenciaCondicaoEspecial(state && state.condicoesAplicadas);
  if (temProgramadas && mesesCarenciaCE > 0) {
    erros.push(
      'Não é possível seguir com a inclusão de Parcelas Programadas. ' +
      'É preciso remover a condição especial de carência para incluí-las.'
    );
  }
  if (financiamento && financiamento.quantidade_parcelas > 0 && mesesCarenciaCE > 0) {
    var vencimentoMinimo = calcularVencimentoFinanciamentoDefault(
      state.dataBase,
      state.pagamentosIncorp,
      state.pagamentosMesa,
      state.condicoesAplicadas,
      state.parcelasEmSeries
    );
    var vencimentoInformado = financiamento.vencimento ? moment(financiamento.vencimento) : null;
    if (vencimentoInformado && vencimentoInformado.isValid() && vencimentoInformado.isBefore(moment(vencimentoMinimo), 'day')) {
      erros.push(
        'Vencimento da primeira parcela de financiamento deve respeitar a carência da condição especial. ' +
        'Data mínima: ' + formatarDataBR(vencimentoMinimo) + '.'
      );
    }
  }

  return erros;
}

// Calcula o vencimento padrão da 1ª parcela do financiamento — usado no input
// "Vencimento" do plano de financiamento. Lógica:
//   - Sem sinal: dataVenda + carência CE + 1 mês (dia 20)
//   - Com sinal: último vencimento do sinal + carência CE + 1 mês (dia 20)
//   - Com programadas: último vencimento das programadas + 1 mês (dia 20)
// Sem carência CE, o financiamento começa direto no mês seguinte à última parcela anterior
// (sem gap), exatamente como o usuário pediu.
export function calcularVencimentoFinanciamentoDefault(
  dataBase,
  pagamentosIncorp,
  pagamentosMesa,
  condicoesAplicadas,
  parcelasEmSeries
) {
  var temProgramadas = parcelasEmSeries && parcelasEmSeries.length > 0;
  var carenciaMeses = temProgramadas ? 0 : obterMesesCarenciaCondicaoEspecial(condicoesAplicadas);

  var ultimoVenc = obterUltimoVencimentoParcelas([
    pagamentosIncorp,
    pagamentosMesa,
    parcelasEmSeries,
  ]);
  var base = ultimoVenc ? moment(ultimoVenc) : moment(dataBase);
  return base.add(carenciaMeses + 1, 'months').date(20).format('YYYY-MM-DD');
}

// W-0075 (devolução SINAL pt.1): payload ENXUTO para imprimir a Proposta a partir da
// simulação (sem gravar venda, sem cliente). Diferente de simulatorStateToVendaPayload,
// manda só o que o documento precisa — sem divisão de comissão, condições especiais,
// autorização ou prospecto. Backend: POST /contratos/gerar-proposta-simulador-pdf.
export function simulatorStateToPropostaPayload(state) {
  var dados = state.dados || {};
  var codigoAtivo = state.codigoAtivo || dados.codigo || null;
  var financiamentoPlano = state.financiamentoPlano || { qtd: 0 };
  var pagamentosIncorp = state.pagamentosIncorp || [];
  var pagamentosMesa = state.pagamentosMesa || [];
  var escadinha = state.parcelasEmSeries || [];
  var intermediarias = state.parcelasIntermediariasManuais || [];
  var condicionantes = state.condicionantesAplicadas || [];

  var vencFin = state.vencimentoFinanciamento
    || calcularVencimentoFinanciamentoDefault(
      state.dataBase,
      pagamentosIncorp,
      pagamentosMesa,
      state.condicoesAplicadas,
      escadinha
    );

  var mapSinal = function(p) {
    var cartaoObj = p.cartao && typeof p.cartao === 'object' ? p.cartao : null;
    return {
      valor: r2(p.valor || 0),
      vencimento: formatarData(p.vencimento),
      tipo_parcela: p.tipo_parcela,
      cartao_codigo: p.tipo_parcela === CARTAO
        ? (cartaoObj ? cartaoObj.codigo : (p.cartao || p.codigo || null))
        : null,
    };
  };

  return {
    codigo_familia: state.codigoFamilia || (dados ? dados.codigo_familia : null) || null,
    codigo_ativo: codigoAtivo,
    data: formatarData(state.dataBase),
    is_sac: !!state.isSac,
    sinal: pagamentosIncorp.map(mapSinal),
    sinal_mesa: pagamentosMesa.map(mapSinal),
    financiamento: {
      quantidade_parcelas: financiamentoPlano.qtd || 0,
      valor: r2(financiamentoPlano.valorParcela || 0),
      vencimento: vencFin,
    },
    escadinha: escadinha.map(function(p) {
      return { valor: r2(p.valor || 0), vencimento: formatarData(p.vencimento) };
    }),
    intermediarias: intermediarias.map(function(p) {
      return { valor: r2(p.valorFuturo || p.valor || 0), vencimento: formatarData(p.vencimento) };
    }),
    condicionantes: condicionantes.map(function(c) {
      var isConstrucao = c.tipo && String(c.tipo).toLowerCase().indexOf('constru') >= 0;
      return {
        valor: r2(c.valorFuturo || c.valor || 0),
        vencimento: formatarData(c.vencimento),
        periodicidade_balao: isConstrucao ? 1 : 2,
      };
    }),
  };
}

// Validações da PÁGINA do simulador (antes de abrir o modal "Gerar Venda").
// Roda no clique do botão "Gerar Venda" da página principal — se houver erros, exibe
// na própria página e NÃO abre o modal. Filtra erros que dependem do modal:
//   - "Lote não selecionado" → ainda valida (já tem na página)
//   - "Plano de financiamento não selecionado" → ainda valida
//   - "Sinal..." → ainda valida
//   - "Condições especiais — autorizador/justificativa" → NÃO valida aqui (vem do modal)
//
// Espera `state.codigoFamilia` populado pelo chamador (vem de contextoSelecao no index.js).
// O `validarPayloadAntesSubmit` continua sendo usado dentro do modal para validar os
// campos preenchidos lá (autorizador, senha, justificativa).
export function validarPayloadAntesAbrirModal(state) {
  // Monta um payload "incompleto" com state da página. Como o modal ainda não foi
  // aberto, não há prospecto/cartões — populamos só o que o payload exige para
  // calcular corretamente: codigo da família e tipo de comissão.
  var payloadParcial;
  try {
    payloadParcial = simulatorStateToVendaPayload(
      Object.assign({}, state, {
        contextoVenda: {
          empreendimento: {
            codigo: state.codigoFamilia,
            tipo_recebimento_comissao: (state.dados && state.dados.tipo_recebimento_comissao) || 0,
          },
        },
        autorizacao: { justificativa: '', codigo_usuario: '', senha: '' },
      })
    );
  } catch (e) {
    return ['Estado da simulação incompleto: ' + (e.message || e)];
  }
  var todosErros = validarPayloadAntesSubmit(payloadParcial, state);
  // Remove erros que só fazem sentido dentro do modal (CE + autorizador, Corretor).
  // Corretor é selecionado no AdminCampos do modal (W-0075), não na página.
  return todosErros.filter(function(msg) {
    return msg.indexOf('autorizador') === -1
      && msg.indexOf('justificativa') === -1
      && msg.indexOf('Corretor não informado') === -1;
  });
}
